import { useState } from 'react';
import { Button } from '../button/button.tsx';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import type { MeetingTemplateDraft } from './meeting-template-types.ts';

interface TemplateFormActionsProps {
  /** The template being edited, or null while writing a new one. */
  template: MeetingTemplateDraft | null;
  /** Enough is filled in to save. */
  complete: boolean;
  canDelete: boolean;
  /** Set, the delete stays off and this line says why. */
  deleteRefusal?: string;
  saving: boolean;
  error?: string;
  labels: MeetingTemplateEditorLabels;
  onSave: () => void;
  onDelete?: () => void | Promise<void>;
}

/** Save, and delete behind a confirmation, under the template form. */
export function TemplateFormActions({
  template,
  complete,
  canDelete,
  deleteRefusal,
  saving,
  error,
  labels,
  onSave,
  onDelete,
}: TemplateFormActionsProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <>
      <div className='flex items-center gap-3'>
        <Button disabled={saving || !complete} onClick={onSave}>
          {template ? labels.save : labels.create}
        </Button>
        {template && canDelete && (
          <Button
            variant='dangerQuiet'
            disabled={saving || Boolean(deleteRefusal)}
            onClick={() => setConfirmingDelete(true)}
          >
            {labels.delete}
          </Button>
        )}
        {template && canDelete && deleteRefusal && (
          <p className='text-sm text-ink-muted'>{deleteRefusal}</p>
        )}
        {error && (
          <p role='alert' className='text-sm text-danger'>
            {error}
          </p>
        )}
      </div>

      {template && canDelete && (
        <ConfirmDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={labels.deleteTitle}
          description={labels.deleteDescription({ name: template.name })}
          confirmLabel={labels.deleteConfirm}
          cancelLabel={labels.deleteCancel}
          variant='danger'
          onConfirm={async () => {
            /* A failure is the host's to report, through `error`. */
            try {
              await onDelete?.();
            } finally {
              setConfirmingDelete(false);
            }
          }}
        />
      )}
    </>
  );
}
